import { FilterOptionsResponse, DashboardResponse, PrefectureDashboardResponse } from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const API_KEY = import.meta.env.VITE_API_KEY

// 環境変数が設定されていない場合のエラーハンドリング
if (!API_BASE_URL) {
  throw new Error('VITE_API_BASE_URL environment variable is not set')
}
if (!API_KEY) {
  throw new Error('VITE_API_KEY environment variable is not set')
}

const headers = {
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
}

export const api = {
  // フィルター選択肢取得
  async getFilterOptions(): Promise<FilterOptionsResponse> {
    // タイムアウトを30秒に設定（API Gatewayのタイムアウトに合わせる）
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30秒
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/filters/options`, {
        headers,
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`)
      }
      
      return response.json()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout: The request took longer than 30 seconds')
      }
      throw error
    }
  },

  // ダッシュボードデータ取得
  async getDashboardData(params: {
    category?: string
    startYear?: number
    endYear?: number
    prefectureIds?: number[]
    teamIds?: number[]
    achievementType?: string
  }): Promise<DashboardResponse> {
    const searchParams = new URLSearchParams()
    
    if (params.category) {
      searchParams.append('category', params.category)
    }
    if (params.startYear) {
      searchParams.append('start_year', params.startYear.toString())
    }
    if (params.endYear) {
      searchParams.append('end_year', params.endYear.toString())
    }
    if (params.prefectureIds && params.prefectureIds.length > 0) {
      searchParams.append('prefecture_ids', params.prefectureIds.join(','))
    }
    if (params.teamIds && params.teamIds.length > 0) {
      searchParams.append('team_ids', params.teamIds.join(','))
    }
    if (params.achievementType) {
      searchParams.append('achievement_type', params.achievementType)
    }

    const url = `${API_BASE_URL}/api/dashboard?${searchParams.toString()}`
    
    // デバッグ用ログ
    console.log('API request URL:', url)
    
    // タイムアウトを30秒に設定（API Gatewayのタイムアウトに合わせる）
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30秒
    
    try {
      const response = await fetch(url, {
        headers,
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`)
      }
      
      return response.json()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout: The request took longer than 30 seconds')
      }
      throw error
    }
  },

  // 都道府県別ダッシュボードデータ取得（新規追加）
  async getPrefectureDashboardData(params: {
    startYear?: number
    endYear?: number
    prefectureIds?: number[]
    generation?: string
  }): Promise<PrefectureDashboardResponse> {
    const searchParams = new URLSearchParams()
    
    if (params.startYear) {
      searchParams.append('start_year', params.startYear.toString())
    }
    if (params.endYear) {
      searchParams.append('end_year', params.endYear.toString())
    }
    if (params.prefectureIds && params.prefectureIds.length > 0) {
      searchParams.append('prefecture_ids', params.prefectureIds.join(','))
    }
    if (params.generation) {
      searchParams.append('generation', params.generation)
    }

    const url = `${API_BASE_URL}/api/prefecture-dashboard?${searchParams.toString()}`
    
    // デバッグ用ログ
    console.log('Prefecture Dashboard API request URL:', url)
    
    // タイムアウトを30秒に設定（API Gatewayのタイムアウトに合わせる）
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30秒
    
    try {
      const response = await fetch(url, {
        headers,
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`)
      }
      
      return response.json()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout: The request took longer than 30 seconds')
      }
      throw error
    }
  },

  // デバッグ用: 都道府県データ確認
  async getDebugPrefectures(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/dashboard/debug/prefectures`, {
      headers,
    })
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`)
    }
    
    return response.json()
  },
}